import React, { useState } from "react";
import { Link } from "../components/L";
import axios from "axios";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import PhoneField from "../components/PhoneField";
import { useLanguage } from "../i18n/LanguageContext";
import useSEO from "../hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, tx } = useLanguage();

  useSEO({
    title: tx("Kontakt - Na Kontaktoni"),
    description: tx("Kontaktoni Albanian Aligners Academy - info@aligners.academy, +355 696056794. Adresa: iClear Group, Rruga Albanopoli 43, 1016, Tirane, Shqiperi."),
    keywords: "kontakt aligners academy, adresa tirane, numri telefonit, email kontakt, na kontaktoni",
    canonical: "https://aligners.academy/kontakt",
  });
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.first_name || !form.last_name || !form.email || !form.message) {
      setError(tx("Ju lutem plotesoni te gjitha fushat e detyrueshme."));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setSubmitted(true);
      setForm({ first_name: "", last_name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map((d) => d.msg).join(", ")
        : tx("Ka ndodhur nje gabim. Ju lutem provoni perseri.");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Banner */}
      <section className="relative py-10 md:py-14 px-6" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)' }} data-testid="contact-hero">
        <div className="max-w-[1350px] mx-auto">
          <h1 className="text-white mb-3" style={{ fontFamily: "'Noto Sans', sans-serif", fontWeight: 700, fontSize: '48px', lineHeight: 1.2 }} data-testid="contact-title">
            {tx("Kontakt")}
          </h1>
          <p className="text-white text-base md:text-lg max-w-2xl">
            {tx("Na dergoni pyetjet tuaja ose kerkesat per bashkepunim. Jemi ketu per t'ju ndihmuar!")}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[50px] max-w-[1350px] mx-auto">
          {/* Contact Info */}
          <div data-testid="contact-info">
            <div className="h-full" style={{ borderRadius: '5px', margin: '15px', background: '#ffffff', padding: '50px 35px', boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.06)' }}>
              <h2 className="text-[#1CA3B0] text-2xl font-bold tracking-tight mb-0" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
                {tx("Informacion Kontakti")}
              </h2>
              <hr className="border-slate-200 my-5" />

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(29, 136, 168, 0.1)' }}>
                    <i className="fas fa-map-marker-alt" style={{ color: '#1d88a8' }}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0A0D10] text-sm mb-1">{tx("Adresa")}</p>
                    <p className="text-sm text-[#4A5568]">{tx("Rruga Albanopoli 43, 1016, Tirane, Shqiperi")}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(29, 136, 168, 0.1)' }}>
                    <i className="fas fa-phone" style={{ color: '#1d88a8' }}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0A0D10] text-sm mb-1">{tx("Nr. Cel")}</p>
                    <a href="tel:+355696056794" className="text-sm text-[#4A5568] hover:text-[#08707A] transition-colors">+355 696056794</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(29, 136, 168, 0.1)' }}>
                    <i className="fas fa-envelope" style={{ color: '#1d88a8' }}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0A0D10] text-sm mb-1">Email</p>
                    <a href="mailto:info@aligners.academy" className="text-sm text-[#4A5568] hover:text-[#08707A] transition-colors">info@aligners.academy</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(29, 136, 168, 0.1)' }}>
                    <i className="fas fa-clock" style={{ color: '#1d88a8' }}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0A0D10] text-sm mb-1">{tx("Orari")}</p>
                    <p className="text-sm text-[#4A5568]">{tx("e Hene - e Shtune | 08:00 - 17:00")}</p>
                  </div>
                </div>
              </div>

              {/* Na Ndiqni */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h3 className="text-[#1CA3B0] text-xl font-bold mb-2" style={{ fontFamily: "'Noto Sans', sans-serif" }}>{tx("Na Ndiqni")}</h3>
                <p className="text-sm text-[#4A5568] mb-4">{tx("Na ndiqni ne rrjete sociale per lajme dhe eventet e reja.")}</p>
                <div className="flex items-center gap-3">
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors" style={{ backgroundColor: 'rgba(29, 136, 168, 0.1)', color: '#1d88a8' }} data-testid="contact-social-facebook"><i className="fab fa-facebook-f"></i></a>
                  <a href="https://www.instagram.com/albanian.aligners.academy/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors" style={{ backgroundColor: 'rgba(29, 136, 168, 0.1)', color: '#1d88a8' }} data-testid="contact-social-instagram"><i className="fab fa-instagram"></i></a>
                  <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors" style={{ backgroundColor: 'rgba(29, 136, 168, 0.1)', color: '#1d88a8' }} data-testid="contact-social-linkedin"><i className="fab fa-linkedin-in"></i></a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors" style={{ backgroundColor: 'rgba(29, 136, 168, 0.1)', color: '#1d88a8' }} data-testid="contact-social-twitter"><i className="fab fa-x-twitter"></i></a>
                </div>
              </div>
            </div>
          </div>
          <div data-testid="contact-form-wrapper">
            <div className="h-full" style={{ borderRadius: '5px', margin: '15px', background: '#ffffff', padding: '50px 35px', boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.06)' }}>
              <h2 className="text-[#1CA3B0] text-2xl font-bold tracking-tight mb-0" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
                {tx("Forme Kontakti")}
              </h2>
              <hr className="border-slate-200 my-5" />

              {submitted ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <CheckCircle className="mx-auto text-[#1CA3B0] mb-4" size={56} />
                  <h3 className="text-xl font-bold text-[#0A0D10] mb-2">{tx("Faleminderit!")}</h3>
                  <p className="text-[#4A5568]">{tx("Mesazhi juaj u dergua me sukses. Do t'ju kontaktojme se shpejti.")}</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-[#08707A] font-semibold text-sm hover:underline"
                    data-testid="send-another-btn"
                  >
                    {tx("Dergo nje mesazh tjeter")}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"><i className="fas fa-user text-sm"></i></span>
                      <Input
                        id="first_name"
                        name="first_name"
                        value={form.first_name}
                        onChange={handleChange}
                        placeholder={tx("Emer *")}
                        className="rounded-lg border-slate-200 bg-slate-50 pl-10 pr-4 py-3 focus:ring-2 focus:ring-[#1CA3B0]/50 focus:border-[#1CA3B0]"
                        data-testid="input-first-name"
                      />
                    </div>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"><i className="fas fa-user text-sm"></i></span>
                      <Input
                        id="last_name"
                        name="last_name"
                        value={form.last_name}
                        onChange={handleChange}
                        placeholder={tx("Mbiemer *")}
                        className="rounded-lg border-slate-200 bg-slate-50 pl-10 pr-4 py-3 focus:ring-2 focus:ring-[#1CA3B0]/50 focus:border-[#1CA3B0]"
                        data-testid="input-last-name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"><i className="fas fa-envelope text-sm"></i></span>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="Email *"
                        className="rounded-lg border-slate-200 bg-slate-50 pl-10 pr-4 py-3 focus:ring-2 focus:ring-[#1CA3B0]/50 focus:border-[#1CA3B0]"
                        data-testid="input-email"
                      />
                    </div>
                    <div className="relative">
                      <PhoneField
                        value={form.phone}
                        onChange={(val) => setForm((f) => ({ ...f, phone: val.startsWith("+") ? val : `+${val}` }))}
                        placeholder={tx("Nr. Cel *")}
                        dataTestId="input-phone"
                      />
                    </div>
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"><i className="fas fa-pen text-sm"></i></span>
                    <Input
                      id="subject"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder={tx("Subjekti")}
                      className="rounded-lg border-slate-200 bg-slate-50 pl-10 pr-4 py-3 focus:ring-2 focus:ring-[#1CA3B0]/50 focus:border-[#1CA3B0]"
                      data-testid="input-subject"
                    />
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-3.5 text-slate-400"><i className="fas fa-edit text-sm"></i></span>
                    <Textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder={tx("Mesazhi juaj")}
                      rows={5}
                      className="rounded-lg border-slate-200 bg-slate-50 pl-10 pr-4 py-3 focus:ring-2 focus:ring-[#1CA3B0]/50 focus:border-[#1CA3B0]"
                      data-testid="input-message"
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm" data-testid="contact-error">{error}</p>
                  )}

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={form.terms || false}
                      onChange={(e) => setForm({ ...form, terms: e.target.checked })}
                      className="mt-1 rounded border-slate-300 text-[#08707A] focus:ring-[#1CA3B0]"
                      data-testid="terms-checkbox"
                    />
                    <label htmlFor="terms" className="text-sm text-[#4A5568]">
                      {tx("Duke vazhduar, une pranoj")}{' '}
                      <a href="/kushtet" className="text-[#1CA3B0] hover:underline font-medium">{tx("Kushtet e Perdorimit")}</a>
                      {' '}{tx("dhe")}{' '}
                      <a href="/privatesia" className="text-[#1CA3B0] hover:underline font-medium">{tx("Politika e Privatesise")}</a>.
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-lg px-8 py-3 font-semibold transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2 bg-[#2D3748] text-white shadow-md hover:bg-[#1A202C] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    data-testid="contact-submit-button"
                  >
                    {submitting ? tx("Duke derguar...") : tx("Dergo")} <i className="fas fa-arrow-right text-sm"></i>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Google Map */}
      <section className="w-full" data-testid="contact-map">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4953.642643435339!2d19.807617399999998!3d41.339558!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x135031a69ad9bdf9%3A0xb493b20770bbbf4a!2siClear%20Aligners!5e0!3m2!1ssq!2sde!4v1775171764225!5m2!1ssq!2sde"
          width="100%"
          height="450"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="iClear Aligners Location"
        />
      </section>
    </div>
  );
};

export default ContactPage;
